'use client'

import type { EventPublishProps } from '@maestro/core/event/types'
import type { JSX } from 'react'

import { useEffect, useId, useRef, useState } from 'react'

import { useGlobalContext } from '@maestro/commerce-blocks/context/global.context'
import { useSessionReady } from '@maestro/commerce-blocks/providers/maestro-session/hooks/use-session-ready'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { type EventPayloadType, EventType } from '@maestro/data-layer'

export const EventPublish = <T extends EventType>({
  payload,
  event,
  isAlgoliaSpecific = false,
  indexOverride,
  observeVisibility = false,
}: EventPublishProps<T>): JSX.Element | null => {
  const {
    publish,
    subscribeTo,
    isPageViewPublished: getIsPageViewPublished,
  } = useEventBusContext()
  const { session } = useMaestroSession()
  const { preloadedProductData, searchProductIndex } = useGlobalContext()
  const isSessionReady = useSessionReady()
  const hasPublished = useRef(false)
  const id = useId()
  const containerRef = useRef<HTMLSpanElement>(null)
  const [isPageViewPublished, setIsPageViewPublished] = useState(() =>
    getIsPageViewPublished()
  )
  const [isVisible, setIsVisible] = useState(!observeVisibility)

  useEffect(() => {
    if (getIsPageViewPublished()) {
      setIsPageViewPublished(true)
      return
    }
    const obs = subscribeTo<EventPayloadType[EventType.PAGE_VIEW]>(
      EventType.PAGE_VIEW,
      `event-publish-${id}`
    )
    if (!obs) {
      return
    }
    const sub = obs.subscribe(() => setIsPageViewPublished(true))
    return () => {
      sub.unsubscribe()
    }
  }, [id, getIsPageViewPublished, subscribeTo])

  useEffect(() => {
    if (!observeVisibility) {
      return
    }
    const el = containerRef.current?.parentElement
    if (!el) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [observeVisibility])

  useEffect(() => {
    if (hasPublished.current) {
      return
    }

    if (isAlgoliaSpecific && !isSessionReady) {
      return
    }

    if (!isPageViewPublished) {
      return
    }

    if (!isVisible) {
      return
    }

    const eventPayload: EventPayloadType[T] = (
      isAlgoliaSpecific
        ? {
            ...payload,
            listId: preloadedProductData.eventListProps?.listId,
            listName: preloadedProductData.eventListProps?.listName,
            searchServiceIndex:
              indexOverride ||
              preloadedProductData.eventSearchProps?.searchIndex ||
              searchProductIndex,
            authenticatedUserToken: session?.user?.hashedEmail ?? '',
          }
        : {
            ...payload,
          }
    ) as EventPayloadType[T]

    const timer = setTimeout(() => {
      hasPublished.current = true
      publish<EventPayloadType[T]>(event, eventPayload)
    }, 200)

    return () => clearTimeout(timer)
  }, [
    event,
    indexOverride,
    isAlgoliaSpecific,
    isPageViewPublished,
    isSessionReady,
    isVisible,
    payload,
    publish,
    session?.user?.hashedEmail,
    preloadedProductData.eventSearchProps?.searchIndex,
    searchProductIndex,
    preloadedProductData.eventListProps?.listId,
    preloadedProductData.eventListProps?.listName,
  ])

  return observeVisibility ? (
    <span ref={containerRef} aria-hidden="true" className="sr-only" />
  ) : null
}
